import React, {
  useState,
  useRef,
  useEffect,
  ClipboardEvent,
  KeyboardEvent,
} from "react";
import styles from "./OtpInput.module.css";

export type OtpStatus = "idle" | "loading" | "success" | "error";

interface OtpInputProps {
  length?: number;
  status: OtpStatus;
  onComplete: (code: string) => void;
  onReset: () => void;
  timeLeft?: number;
  tick?: boolean;
  resending?: boolean;
  onResend?: () => void;
  resendLabel?: string;
  resendBtnLabel?: string;
  resendingNode?: React.ReactNode;
  successMessage?: string;
  avatarNode?: React.ReactNode;
  title?: string;
  subtitle?: React.ReactNode;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  status,
  onComplete,
  onReset,
  timeLeft,
  tick,
  resending,
  onResend,
  resendLabel = "Send code again",
  resendBtnLabel = "Resend code",
  resendingNode,
  successMessage = "Verification successfully",
  avatarNode,
  title,
  subtitle,
}) => {
  const [code, setCode] = useState<string[]>(new Array(length).fill(""));
  const [phase, setPhase] = useState<"idle" | "tilt" | "merge">("idle");
  const [successVisible, setSuccessVisible] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (code.every((d) => d !== "") && status === "idle") {
      onComplete(code.join(""));
    }
  }, [code, status, onComplete]);

  useEffect(() => {
    if (status === "error") {
      const t = setTimeout(() => {
        setCode(new Array(length).fill(""));
        setPhase("idle");
        onReset();
        inputsRef.current[0]?.focus();
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [status, length, onReset]);

  useEffect(() => {
    if (status === "success") {
      setSuccessVisible(false);
      setPhase("idle");

      // Step 1: tilt after short delay
      const t1 = setTimeout(() => setPhase("tilt"), 150);
      // Step 2: merge (fly to center + fade out)
      const t2 = setTimeout(() => setPhase("merge"), 750);
      // Step 3: show checkmark + message after cells are gone
      const t3 = setTimeout(() => setSuccessVisible(true), 1300);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    } else {
      setPhase("idle");
      setSuccessVisible(false);
    }
  }, [status]);

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (code[index] === "" && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputsRef.current[index - 1]?.focus();
      } else {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .slice(0, length)
      .replace(/\D/g, "");
    if (pastedData) {
      const newCode = [...code];
      pastedData.split("").forEach((char, i) => {
        newCode[i] = char;
      });
      setCode(newCode);
      const focusIndex = Math.min(pastedData.length, length - 1);
      inputsRef.current[focusIndex]?.focus();
    }
  };

  const isLoading = status === "loading";
  const isSuccess = status === "success";
  const isError = status === "error";
  const isLocked = isLoading || isSuccess;

  // Each cell tilts inward toward center (left cells lean right, right cells lean left)
  const getTiltAngle = (index: number): number => {
    const center = (length - 1) / 2;
    const dist = index - center;
    return -dist * 14;
  };

  // Each cell translates toward center on merge
  const getMergeTranslateX = (index: number): number => {
    const center = (length - 1) / 2;
    const dist = index - center;
    return -dist * 38;
  };

  const hasResend = onResend !== undefined && timeLeft !== undefined;

  return (
    <div className={styles.outerWrapper}>
      {/* Success message */}
      <div
        className={`${styles.successMessage} ${successVisible ? styles.successMessageVisible : ""}`}
      >
        <h2 className={styles.title}>{successMessage}</h2>
      </div>

      {/* Header */}
      {(avatarNode || title || subtitle) && (
        <div
          className={`${styles.headerArea} ${isSuccess ? styles.headerHidden : ""}`}
        >
          {avatarNode && <div className={styles.avatarWrap}>{avatarNode}</div>}
          {title && <h2 className={styles.title}>{title}</h2>}
          {subtitle &&
            (typeof subtitle === "string" ? (
              <p
                className={styles.subtitle}
                dangerouslySetInnerHTML={{ __html: subtitle }}
              />
            ) : (
              <div className={styles.subtitle}>{subtitle}</div>
            ))}
        </div>
      )}

      {/* OTP cells + success overlay */}
      <div className={styles.mainWrapper}>
        <div
          className={`${styles.inputsContainer} ${
            phase === "merge" ? styles.inputsHidden : ""
          }`}
        >
          {code.map((digit, i) => {
            const tilt = getTiltAngle(i);
            const tx = phase === "merge" ? getMergeTranslateX(i) : 0;

            let cellStyle: React.CSSProperties = {};
            if (phase === "tilt") {
              cellStyle = { transform: `rotate(${tilt}deg)` };
            } else if (phase === "merge") {
              cellStyle = {
                transform: `translateX(${tx}px) rotate(${tilt}deg)`,
              };
            }

            return (
              <div
                key={i}
                className={`
                  ${styles.cellWrap}
                  ${phase === "tilt" ? styles.phaseTilt : ""}
                  ${phase === "merge" ? styles.phaseMerge : ""}
                `}
                style={cellStyle}
              >
                <input
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onPaste={handlePaste}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  autoFocus={i === 0}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`
                    ${styles.input}
                    ${digit ? styles.inputFilled : ""}
                    ${isError ? styles.inputError : ""}
                    ${isLoading ? styles.inputLoading : ""}
                  `}
                  disabled={isLocked || isError}
                />

                {isLoading && (
                  <svg
                    className={styles.orbitSvg}
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient
                        id={`og${i}`}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor="#020637" stopOpacity={0} />
                        <stop offset="65%" stopColor="#020637" />
                        <stop offset="100%" stopColor="#2a4fc0" />
                      </linearGradient>
                    </defs>
                    <rect
                      className={styles.orbitTrack}
                      x="1"
                      y="1"
                      width="64"
                      height="64"
                      rx="16"
                    />
                    <rect
                      className={styles.orbitTrail}
                      x="1"
                      y="1"
                      width="64"
                      height="64"
                      rx="16"
                      pathLength="220"
                      stroke={`url(#og${i})`}
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  </svg>
                )}
              </div>
            );
          })}
        </div>

        {/* Success checkmark */}
        <div
          className={`${styles.successContainer} ${successVisible ? styles.successVisible : ""}`}
        >
          <div className={styles.checkIconGlow}>
            <svg viewBox="0 0 50 50" className={styles.checkSvg}>
              <path
                className={`${styles.checkPath} ${successVisible ? styles.checkPathAnimate : ""}`}
                fill="none"
                d="M14 27l7 7 16-16"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Resend */}
      {hasResend && !isSuccess && (
        <div className={styles.resendArea}>
          {(timeLeft as number) > 0 ? (
            <p className={styles.resendWrapper}>
              <span className={styles.resendLabel}>{resendLabel}</span>
              <span
                className={`${styles.resendTimer} ${tick ? styles.resendTimerTick : ""}`}
              >
                00:{(timeLeft as number).toString().padStart(2, "0")}
              </span>
            </p>
          ) : resending ? (
            resendingNode ? (
              <>{resendingNode}</>
            ) : (
              <p className={styles.resendLabel}>Sending…</p>
            )
          ) : (
            <button
              className={styles.resendBtn}
              onClick={onResend}
              disabled={isLoading}
              type="button"
            >
              {resendBtnLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};